import React, { useState, useEffect } from 'react';
import {
    Card,
    Text,
    Button,
    Dropdown,
    Option,
    Badge,
    Dialog,
    DialogTrigger,
    DialogSurface,
    DialogTitle,
    DialogContent,
    DialogBody,
    DialogActions,
    Input,
    Textarea,
    Spinner,
} from '@fluentui/react-components';
import {
    Calendar24Regular,
    Person24Regular,
    Clock24Regular,
    Add24Regular,
    PersonArrowRight24Regular,
    Alert24Regular,
    CheckmarkCircle24Regular,
} from '@fluentui/react-icons';
import { apiService } from '../../../services/api/apiService';
import { socketService } from '../../../services/socket/socketService';

interface Doctor {
    id: string;
    firstName: string;
    lastName: string;
    specialty: string;
    isAvailable: boolean;
    currentConsults: number;
    maxConsults: number;
    shift: 'morning' | 'afternoon' | 'evening' | 'night';
}

interface ConsultRequest {
    id: string;
    patientName: string;
    patientId: string;
    mrn: string;
    priority: 'urgent' | 'standard' | 'routine';
    consultationType: string;
    requestedTime: string;
    language: string;
    interpreterRequired: boolean;
    deviceId?: string;
    status: 'unassigned' | 'assigned' | 'in_progress' | 'completed';
    assignedDoctorId?: string;
    epicEncounterId?: string;
}

interface TimeSlot {
    time: string;
    consultId?: string;
    patientName?: string;
    status?: 'scheduled' | 'in_progress' | 'available';
}

const ScheduleManagement: React.FC = () => {
    const [doctors, setDoctors] = useState<Doctor[]>([]);
    const [consultRequests, setConsultRequests] = useState<ConsultRequest[]>([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(true);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedConsult, setSelectedConsult] = useState<ConsultRequest | null>(null);
    const [selectedDoctor, setSelectedDoctor] = useState<string>('');
    const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

    const timeSlots = [
        '08:00',
        '08:30',
        '09:00',
        '09:30',
        '10:00',
        '10:30',
        '11:00',
        '11:30',
        '12:00',
        '12:30',
        '13:00',
        '13:30',
        '14:00',
        '14:30',
        '15:00',
        '15:30',
        '16:00',
        '16:30',
        '17:00',
        '17:30',
        '18:00',
        '18:30',
        '19:00',
        '19:30',
    ];

    useEffect(() => {
        loadScheduleData();
        // Set up real-time updates
        socketService.connect();
        socketService.on('consultAssigned', handleConsultAssigned);
        socketService.on('consultStatusUpdated', handleConsultStatusUpdated);

        return () => {
            socketService.off('consultAssigned');
            socketService.off('consultStatusUpdated');
        };
    }, [selectedDate]);

    const loadScheduleData = async () => {
        try {
            setLoading(true);
            const [doctorsData, consultsData] = await Promise.all([
                apiService.getDoctorAvailability(selectedDate),
                apiService.getConsultRequests(selectedDate),
            ]);

            setDoctors(doctorsData);
            setConsultRequests(consultsData);
        } catch (error) {
            console.error('Error loading schedule data:', error);
            // Fallback data for demo
            setDoctors([
                {
                    id: '1',
                    firstName: 'Dr. Sarah',
                    lastName: 'Johnson',
                    specialty: 'Child Psychiatry',
                    isAvailable: true,
                    currentConsults: 2,
                    maxConsults: 6,
                    shift: 'morning',
                },
                {
                    id: '2',
                    firstName: 'Dr. Michael',
                    lastName: 'Chen',
                    specialty: 'Adult Psychiatry',
                    isAvailable: true,
                    currentConsults: 1,
                    maxConsults: 5,
                    shift: 'afternoon',
                },
                {
                    id: '3',
                    firstName: 'Dr. Emily',
                    lastName: 'Rodriguez',
                    specialty: 'Emergency Psychiatry',
                    isAvailable: false,
                    currentConsults: 4,
                    maxConsults: 4,
                    shift: 'evening',
                },
            ]);

            setConsultRequests([
                {
                    id: '1',
                    patientName: 'Maria Gonzalez',
                    patientId: 'P001',
                    mrn: 'MRN-789123',
                    priority: 'urgent',
                    consultationType: 'Crisis Intervention',
                    requestedTime: '09:00',
                    language: 'Spanish',
                    interpreterRequired: true,
                    status: 'unassigned',
                    epicEncounterId: 'ENC-001',
                },
                {
                    id: '2',
                    patientName: 'James Wilson',
                    patientId: 'P002',
                    mrn: 'MRN-456789',
                    priority: 'standard',
                    consultationType: 'Follow-up Assessment',
                    requestedTime: '10:30',
                    language: 'English',
                    interpreterRequired: false,
                    status: 'unassigned',
                    epicEncounterId: 'ENC-002',
                },
                {
                    id: '3',
                    patientName: 'Sophie Dubois',
                    patientId: 'P003',
                    mrn: 'MRN-321654',
                    priority: 'routine',
                    consultationType: 'Initial Consultation',
                    requestedTime: '14:00',
                    language: 'French',
                    interpreterRequired: true,
                    status: 'unassigned',
                    epicEncounterId: 'ENC-003',
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleConsultAssigned = (data: any) => {
        setConsultRequests((prev) =>
            prev.map((consult) =>
                consult.id === data.consultId
                    ? { ...consult, status: 'assigned', assignedDoctorId: data.doctorId }
                    : consult
            )
        );
    };

    const handleConsultStatusUpdated = (data: any) => {
        setConsultRequests((prev) =>
            prev.map((consult) =>
                consult.id === data.consultId ? { ...consult, status: data.status } : consult
            )
        );
    };

    const handleAssignConsult = async () => {
        if (!selectedConsult || !selectedDoctor) return;

        try {
            await apiService.assignConsult(selectedConsult.id, selectedDoctor);

            // Send real-time notification to doctor
            socketService.emit('notifyDoctor', {
                doctorId: selectedDoctor,
                consultId: selectedConsult.id,
                message: `New consultation assigned: ${selectedConsult.patientName}`,
                priority: selectedConsult.priority,
            });

            setAssignDialogOpen(false);
            setSelectedConsult(null);
            setSelectedDoctor('');
            loadScheduleData(); // Refresh data
        } catch (error) {
            console.error('Error assigning consult:', error);
        }
    };

    const getPriorityBadge = (priority: string) => {
        switch (priority) {
            case 'urgent':
                return (
                    <Badge appearance="filled" color="danger">
                        Urgent
                    </Badge>
                );
            case 'standard':
                return (
                    <Badge appearance="tint" color="warning">
                        Standard
                    </Badge>
                );
            case 'routine':
                return (
                    <Badge appearance="outline" color="success">
                        Routine
                    </Badge>
                );
            default:
                return <Badge appearance="outline">Unknown</Badge>;
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'unassigned':
                return (
                    <Badge appearance="outline" color="warning">
                        Unassigned
                    </Badge>
                );
            case 'assigned':
                return (
                    <Badge appearance="tint" color="brand">
                        Assigned
                    </Badge>
                );
            case 'in_progress':
                return (
                    <Badge appearance="filled" color="success">
                        In Progress
                    </Badge>
                );
            case 'completed':
                return (
                    <Badge appearance="ghost" color="subtle">
                        Completed
                    </Badge>
                );
            default:
                return <Badge appearance="outline">Unknown</Badge>;
        }
    };

    const getDoctorSchedule = (doctorId: string): TimeSlot[] => {
        return timeSlots.map((time) => {
            const consult = consultRequests.find(
                (c) => c.assignedDoctorId === doctorId && c.requestedTime === time
            );
            return {
                time,
                consultId: consult?.id,
                patientName: consult?.patientName,
                status: consult
                    ? consult.status === 'in_progress'
                        ? 'in_progress'
                        : 'scheduled'
                    : 'available',
            };
        });
    };

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                }}
            >
                <Spinner size="large" label="Loading schedule..." />
            </div>
        );
    }

    return (
        <div style={{ padding: '0' }}>
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px',
                }}
            >
                <div>
                    <Text size={900} weight="bold">
                        Schedule Management
                    </Text>
                    <Text
                        size={300}
                        style={{ display: 'block', color: '#605e5c', marginTop: '4px' }}
                    >
                        Assign consultations and manage doctor availability
                    </Text>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e, data) => setSelectedDate(data.value)}
                        style={{ minWidth: '160px' }}
                    />
                    <Button appearance="primary" icon={<Add24Regular />}>
                        New Consult Request
                    </Button>
                </div>
            </div>

            {/* Consult Requests Queue */}
            <Card style={{ padding: '24px', marginBottom: '24px' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px',
                    }}
                >
                    <Text size={600} weight="semibold">
                        Consultation Requests (
                        {consultRequests.filter((c) => c.status === 'unassigned').length}{' '}
                        unassigned)
                    </Text>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                    {consultRequests
                        .filter((c) => c.status === 'unassigned')
                        .map((consult) => (
                            <Card
                                key={consult.id}
                                style={{
                                    padding: '16px',
                                    minWidth: '300px',
                                    border:
                                        consult.priority === 'urgent'
                                            ? '2px solid #d13438'
                                            : '1px solid #e1e5e9',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '12px',
                                    }}
                                >
                                    <div>
                                        <Text size={500} weight="semibold">
                                            {consult.patientName}
                                        </Text>
                                        <Text
                                            size={300}
                                            style={{ display: 'block', color: '#605e5c' }}
                                        >
                                            MRN: {consult.mrn} | {consult.requestedTime}
                                        </Text>
                                    </div>
                                    {getPriorityBadge(consult.priority)}
                                </div>

                                <div style={{ marginBottom: '12px' }}>
                                    <Text size={300} style={{ display: 'block' }}>
                                        <strong>Type:</strong> {consult.consultationType}
                                    </Text>
                                    <Text size={300} style={{ display: 'block' }}>
                                        <strong>Language:</strong> {consult.language}
                                        {consult.interpreterRequired && (
                                            <Badge
                                                size="small"
                                                color="warning"
                                                style={{ marginLeft: '8px' }}
                                            >
                                                Interpreter
                                            </Badge>
                                        )}
                                    </Text>
                                </div>

                                <Button
                                    appearance="primary"
                                    size="small"
                                    icon={<PersonArrowRight24Regular />}
                                    onClick={() => {
                                        setSelectedConsult(consult);
                                        setAssignDialogOpen(true);
                                    }}
                                >
                                    Assign Doctor
                                </Button>
                            </Card>
                        ))}
                </div>

                {consultRequests.filter((c) => c.status === 'unassigned').length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#605e5c' }}>
                        <CheckmarkCircle24Regular
                            style={{ fontSize: '48px', marginBottom: '16px' }}
                        />
                        <Text size={500}>All consultation requests have been assigned</Text>
                    </div>
                )}
            </Card>

            {/* Doctor Availability Calendar */}
            <Card style={{ padding: '24px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '20px' }}>
                    Doctor Availability Calendar
                </Text>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                        <thead>
                            <tr>
                                <th style={{ padding: '12px', textAlign: 'left', width: '100px' }}>
                                    Time
                                </th>
                                {doctors.map((doctor) => (
                                    <th
                                        key={doctor.id}
                                        style={{
                                            padding: '12px',
                                            textAlign: 'center',
                                            minWidth: '200px',
                                        }}
                                    >
                                        <div>
                                            <Text size={400} weight="semibold">
                                                {doctor.firstName} {doctor.lastName}
                                            </Text>
                                            <Text
                                                size={200}
                                                style={{ display: 'block', color: '#605e5c' }}
                                            >
                                                {doctor.specialty}
                                            </Text>
                                            <Badge
                                                appearance={doctor.isAvailable ? 'tint' : 'outline'}
                                                color={doctor.isAvailable ? 'success' : 'danger'}
                                                size="small"
                                                style={{ marginTop: '4px' }}
                                            >
                                                {doctor.currentConsults}/{doctor.maxConsults}{' '}
                                                consults
                                            </Badge>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {timeSlots.map((time) => (
                                <tr key={time} style={{ borderBottom: '1px solid #f3f2f1' }}>
                                    <td style={{ padding: '8px', fontWeight: '500' }}>{time}</td>
                                    {doctors.map((doctor) => {
                                        const schedule = getDoctorSchedule(doctor.id);
                                        const slot = schedule.find((s) => s.time === time);
                                        return (
                                            <td
                                                key={`${doctor.id}-${time}`}
                                                style={{ padding: '4px', textAlign: 'center' }}
                                            >
                                                {slot?.status === 'scheduled' && (
                                                    <div
                                                        style={{
                                                            backgroundColor: '#e1f5fe',
                                                            border: '1px solid #0277bd',
                                                            borderRadius: '4px',
                                                            padding: '8px',
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        <Text size={200} weight="semibold">
                                                            {slot.patientName}
                                                        </Text>
                                                    </div>
                                                )}
                                                {slot?.status === 'in_progress' && (
                                                    <div
                                                        style={{
                                                            backgroundColor: '#e8f5e8',
                                                            border: '1px solid #2e7d32',
                                                            borderRadius: '4px',
                                                            padding: '8px',
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        <Text size={200} weight="semibold">
                                                            {slot.patientName}
                                                        </Text>
                                                        <Badge size="extra-small" color="success">
                                                            Live
                                                        </Badge>
                                                    </div>
                                                )}
                                                {slot?.status === 'available' &&
                                                    doctor.isAvailable && (
                                                        <div
                                                            style={{
                                                                backgroundColor: '#f5f5f5',
                                                                border: '1px dashed #ccc',
                                                                borderRadius: '4px',
                                                                padding: '8px',
                                                                fontSize: '12px',
                                                                color: '#666',
                                                            }}
                                                        >
                                                            Available
                                                        </div>
                                                    )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Assignment Dialog */}
            <Dialog
                open={assignDialogOpen}
                onOpenChange={(event, data) => setAssignDialogOpen(data.open)}
            >
                <DialogSurface>
                    <DialogTitle>Assign Consultation</DialogTitle>
                    <DialogContent>
                        <DialogBody>
                            {selectedConsult && (
                                <div style={{ marginBottom: '20px' }}>
                                    <Text size={500} weight="semibold">
                                        {selectedConsult.patientName}
                                    </Text>
                                    <Text size={300} style={{ display: 'block', color: '#605e5c' }}>
                                        {selectedConsult.consultationType} |{' '}
                                        {selectedConsult.requestedTime}
                                    </Text>
                                    {getPriorityBadge(selectedConsult.priority)}
                                </div>
                            )}

                            <Dropdown
                                placeholder="Select a doctor"
                                value={selectedDoctor}
                                onOptionSelect={(event, data) =>
                                    setSelectedDoctor(data.optionValue || '')
                                }
                                style={{ width: '100%', marginBottom: '16px' }}
                            >
                                {doctors
                                    .filter(
                                        (doctor) =>
                                            doctor.isAvailable &&
                                            doctor.currentConsults < doctor.maxConsults
                                    )
                                    .map((doctor) => (
                                        <Option
                                            key={doctor.id}
                                            value={doctor.id}
                                            text={`${doctor.firstName} ${doctor.lastName} - ${doctor.specialty} (${doctor.currentConsults}/${doctor.maxConsults})`}
                                        >
                                            {doctor.firstName} {doctor.lastName} -{' '}
                                            {doctor.specialty} ({doctor.currentConsults}/
                                            {doctor.maxConsults})
                                        </Option>
                                    ))}
                            </Dropdown>

                            <Input
                                placeholder="Enter Device ID (Teams account for device)"
                                value={selectedDeviceId}
                                onChange={(e, data) => setSelectedDeviceId(data.value)}
                                style={{ width: '100%' }}
                            />
                        </DialogBody>
                        <DialogActions>
                            <Button
                                appearance="secondary"
                                onClick={() => setAssignDialogOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleAssignConsult}
                                disabled={!selectedDoctor}
                            >
                                Assign Consultation
                            </Button>
                        </DialogActions>
                    </DialogContent>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default ScheduleManagement;
